import { useState, useMemo } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { startOfDay, subDays, endOfDay, differenceInCalendarDays } from 'date-fns'
import { colors } from '@chillout/core'
import { Gift, LockOpen, Stamp, UserPlus } from 'lucide-react-native'
import { AdminScreen } from '../AdminScreen'
import { DateRangePicker } from '../auditLog/DateRangePicker'
import { StatCard } from './StatCard'
import { LoyaltyTrendsChart } from './LoyaltyTrendsChart'
import { useDashboardStats } from './useDashboardStats'
import { useLoyaltyTrends } from './useLoyaltyTrends'
import { useTotalMembers } from './useTotalMembers'

export function HomeScreen() {
  const { t } = useTranslation()
  const [dateFrom, setDateFrom] = useState(() => startOfDay(subDays(new Date(), 6)))
  const [dateTo, setDateTo] = useState(() => endOfDay(new Date()))

  const comparisonLabel = useMemo(() => {
    const days = differenceInCalendarDays(dateTo, dateFrom) + 1
    return t('adminDashboard.home.stats.vsPrevDays', { count: days })
  }, [dateFrom, dateTo, t])

  const stats = useDashboardStats(dateFrom, dateTo)
  const trends = useLoyaltyTrends(dateFrom, dateTo)
  const totalMembers = useTotalMembers()

  function handleDateChange(from: Date, to: Date) {
    setDateFrom(from)
    setDateTo(to)
  }

  return (
    <AdminScreen
      title={t('adminDashboard.homeTitle')}
      subtitle={t('adminDashboard.homeSubtitle')}
      actions={
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={handleDateChange}
        />
      }
    >
      <View className="gap-6">
        {stats.error ? (
          <View className="items-center rounded-md border border-border bg-canvas-card p-6">
            <Text className="text-sm text-muted">{t('common.errorLoadingData')}</Text>
          </View>
        ) : (
          <View className="gap-4 2xl:flex-row">
            <View className="flex-row gap-4 2xl:flex-1">
              <StatCard
                label={t('adminDashboard.home.stats.stampsAdded')}
                value={stats.data?.stamps_added ?? 0}
                delta={stats.data?.stamps_added_delta ?? 0}
                comparisonLabel={comparisonLabel}
                color={colors.accent.DEFAULT}
                icon={Stamp}
                loading={stats.isLoading}
              />
              <StatCard
                label={t('adminDashboard.home.stats.rewardsRedeemed')}
                value={stats.data?.rewards_redeemed ?? 0}
                delta={stats.data?.rewards_redeemed_delta ?? 0}
                comparisonLabel={comparisonLabel}
                color={colors.warning}
                icon={Gift}
                loading={stats.isLoading}
              />
            </View>
            <View className="flex-row gap-4 2xl:flex-1">
              <StatCard
                label={t('adminDashboard.home.stats.rewardsUnlocked')}
                value={stats.data?.rewards_unlocked ?? 0}
                delta={stats.data?.rewards_unlocked_delta ?? 0}
                comparisonLabel={comparisonLabel}
                color={colors.success}
                icon={LockOpen}
                loading={stats.isLoading}
              />
              <StatCard
                label={t('adminDashboard.home.stats.newMembers')}
                value={stats.data?.new_members ?? 0}
                delta={stats.data?.new_members_delta ?? 0}
                comparisonLabel={comparisonLabel}
                color={colors.role.staff.text}
                icon={UserPlus}
                loading={stats.isLoading}
              />
            </View>
          </View>
        )}

        <LoyaltyTrendsChart
          data={trends.data ?? []}
          dateFrom={dateFrom}
          dateTo={dateTo}
          loading={trends.isLoading}
          error={!!trends.error}
          totalMembers={totalMembers.data ?? 0}
          totalMembersLoading={totalMembers.isLoading}
        />
      </View>
    </AdminScreen>
  )
}